import React, { useEffect, useRef, useState } from "react";
import { ABtn, AResult } from "./arcadeUi";

// Normalized 0..100 space for both axes. Pens are circles for collision.
const R = 8; // collision radius
const START = { yx: 16, yy: 50, cx: 64, cy: 50 };

export default function PenFight() {
  const tableRef = useRef(null);
  const [p, setP] = useState(START);
  const [aim, setAim] = useState(null); // {dx,dy} while dragging
  const [phase, setPhase] = useState("you"); // you | anim | over
  const [result, setResult] = useState(null);
  const raf = useRef(null);
  const drag = useRef(null);
  const st = useRef(START);
  st.current = p;

  const norm = (e) => {
    const r = tableRef.current.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 };
  };

  const down = (e) => {
    if (phase !== "you") return;
    drag.current = norm(e);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    if (!drag.current) return;
    const n = norm(e);
    setAim({ dx: n.x - drag.current.x, dy: n.y - drag.current.y });
  };
  const up = () => {
    if (!drag.current) return;
    const a = aim; drag.current = null; setAim(null);
    if (!a) return;
    const len = Math.hypot(a.dx, a.dy);
    if (len < 3) return;
    const k = 0.16;
    animate({ x: a.dx * k, y: a.dy * k }, { x: 0, y: 0 }, "you");
  };

  const animate = (yv, cv, mover) => {
    setPhase("anim");
    const step = () => {
      let { yx, yy, cx, cy } = st.current;
      yx += yv.x; yy += yv.y; cx += cv.x; cy += cv.y;
      // collision (2D)
      const dx = cx - yx, dy = cy - yy;
      const d = Math.hypot(dx, dy) || 0.001;
      if (d < R * 2) {
        const nx = dx / d, ny = dy / d;
        const yn = yv.x * nx + yv.y * ny; // your speed along normal
        const cn = cv.x * nx + cv.y * ny;
        const t = (yn - cn) * 0.92;
        if (t > 0) {
          cv = { x: cv.x + nx * t, y: cv.y + ny * t };
          yv = { x: yv.x - nx * t, y: yv.y - ny * t };
          const overlap = R * 2 - d;
          yx -= nx * overlap * 0.5; yy -= ny * overlap * 0.5;
          cx += nx * overlap * 0.5; cy += ny * overlap * 0.5;
        }
      }
      yv = { x: yv.x * 0.955, y: yv.y * 0.955 };
      cv = { x: cv.x * 0.955, y: cv.y * 0.955 };
      st.current = { yx, yy, cx, cy };
      setP({ yx, yy, cx, cy });
      const off = (x, y) => x < -6 || x > 106 || y < -10 || y > 110;
      if (off(cx, cy)) { cancelAnimationFrame(raf.current); setResult("win"); setPhase("over"); return; }
      if (off(yx, yy)) { cancelAnimationFrame(raf.current); setResult("lose"); setPhase("over"); return; }
      if (Math.hypot(yv.x, yv.y) < 0.15 && Math.hypot(cv.x, cv.y) < 0.15) {
        cancelAnimationFrame(raf.current);
        if (mover === "you") setTimeout(cpuTurn, 500);
        else { setP(START); st.current = START; setPhase("you"); }
        return;
      }
      raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
  };

  const cpuTurn = () => {
    setP(START); st.current = START;
    setTimeout(() => {
      // aim from cpu toward your pen, with some angle inaccuracy
      const dx = START.yx - START.cx, dy = START.yy - START.cy;
      const ang = Math.atan2(dy, dx) + (Math.random() - 0.5) * 0.5;
      const power = 4.2 + Math.random() * 2.2;
      animate({ x: 0, y: 0 }, { x: Math.cos(ang) * power, y: Math.sin(ang) * power }, "cpu");
    }, 350);
  };

  const newGame = () => { cancelAnimationFrame(raf.current); setP(START); st.current = START; setPhase("you"); setResult(null); setAim(null); };
  useEffect(() => () => cancelAnimationFrame(raf.current), []);

  const penW = 16, penH = 16;
  const power = aim ? Math.min(100, (Math.hypot(aim.dx, aim.dy) / 45) * 100) : 0;

  return (
    <div className="w-full select-none text-center">
      <p className="mb-2 text-sm text-kirana-brown/70">Drag the red pen to set <b>angle + power</b> (an arrow shows your aim), release to flick it and knock the green pen off the desk.</p>

      <div ref={tableRef} data-testid="pf-table" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
        style={{ touchAction: "none" }}
        className="relative mx-auto h-48 w-full max-w-md overflow-hidden rounded-md border-[3px] border-kirana-brown bg-gradient-to-b from-[#a9784f] to-[#8a5e38]">
        <div className="pointer-events-none absolute inset-1 rounded border border-dashed border-white/25" />
        {/* aim arrow */}
        {aim && (
          <svg className="pointer-events-none absolute inset-0 h-full w-full">
            <line x1={`${p.yx}%`} y1={`${p.yy}%`} x2={`${p.yx + aim.dx}%`} y2={`${p.yy + aim.dy}%`} stroke="#fff" strokeWidth="3" strokeDasharray="5 3" />
          </svg>
        )}
        {/* cpu pen */}
        <div data-testid="pf-cpu-pen" className="absolute rounded-full border-2 border-black/40 bg-kirana-green shadow"
          style={{ left: `${p.cx}%`, top: `${p.cy}%`, width: `${penW}%`, height: `${penH}%`, transform: "translate(-50%,-50%)" }} />
        {/* your pen */}
        <div data-testid="pf-your-pen" className="absolute cursor-grab rounded-full border-2 border-black/40 bg-kirana-red shadow active:cursor-grabbing"
          style={{ left: `${p.yx}%`, top: `${p.yy}%`, width: `${penW}%`, height: `${penH}%`, transform: "translate(-50%,-50%)" }} />
      </div>

      {phase === "you" && (
        <div className="mx-auto mt-2 h-2.5 w-full max-w-md overflow-hidden rounded-full border-2 border-kirana-brown bg-kirana-cream">
          <div className="h-full bg-kirana-red" style={{ width: `${power}%` }} data-testid="pf-power" />
        </div>
      )}
      <p className="mt-1 text-xs text-kirana-brown/60">{phase === "you" ? "Aim & flick the red pen →" : phase === "anim" ? "…" : ""}</p>

      {phase === "over" && (
        <>
          <AResult testId="pf-result" win={result === "win"} text={result === "win" ? "Knocked off! You Win 🎉" : "Your pen fell off 😜"} />
          <div className="mt-3 flex justify-center"><ABtn data-testid="pf-again" onClick={newGame} tone="green">Rematch</ABtn></div>
        </>
      )}
    </div>
  );
}
